import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RiAddLine } from "react-icons/ri";
import { fadeUp, staggerContainer, viewportOnce } from "lib/animations";

export interface FAQItem {
  question: string;
  answer: string;
}

interface HomeFAQProps {
  faqs: FAQItem[];
  eyebrow?: string;
  heading?: ReactNode;
  subheading?: string;
}

const HomeFAQ = ({
  faqs,
  eyebrow = "FAQ",
  heading = (
    <>
      Why Clamphook is Nepal&apos;s best IOE &amp;{" "}
      <span className="text-primary">CSIT entrance prep</span>
    </>
  ),
  subheading = "Answers to what students across Nepal ask most before choosing an IOE or CSIT entrance preparation platform.",
}: HomeFAQProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-2xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
            {eyebrow}
          </p>
          <h2 className="mt-2 text-2xl md:text-4xl font-extrabold text-gray-900">
            {heading}
          </h2>
          <p className="mt-3 text-base text-gray-600">{subheading}</p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-8 md:mt-10 divide-y divide-gray-100 border-t border-b border-gray-100"
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div key={faq.question} variants={fadeUp}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="text-base md:text-lg font-semibold text-gray-900">
                    {faq.question}
                  </span>
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary transition-transform duration-300 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  >
                    <RiAddLine className="h-5 w-5" />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 pr-12 text-sm md:text-base leading-relaxed text-gray-600">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default HomeFAQ;
